import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen, ArrowLeft, Clock, Eye, Tag, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSEO } from "@/hooks/useSEO";
import LanguageSelector from "@/components/LanguageSelector";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [blogLang, setBlogLang] = useState((i18n.language || "fi").split("-")[0]);
  const token = localStorage.getItem("token");

  useEffect(() => {
    setBlogLang((i18n.language || "fi").split("-")[0]);
  }, [i18n.language]);

  useEffect(() => {
    fetchPosts();
  }, [blogLang]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedTag) params.append("tag", selectedTag);
      if (blogLang !== "all") params.append("language", blogLang);
      const response = await axios.get(`${API}/blog?${params.toString()}`);
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error("Failed to fetch blog posts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [selectedTag]);

  const allTags = [...new Set(posts.flatMap((p) => p.tags || []))].slice(0, 12);

  const userLang = (i18n.language || "fi").split("-")[0];
  const filteredPosts = posts.filter(
    (p) =>
      p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.excerpt?.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => {
    if (blogLang === "all") {
      // Group by language, user's language first
      const langOrder = ["fi", "en", "fr", "de", "es"];
      const aIdx = langOrder.indexOf(a.language) >= 0 ? langOrder.indexOf(a.language) : 99;
      const bIdx = langOrder.indexOf(b.language) >= 0 ? langOrder.indexOf(b.language) : 99;
      // User's language gets priority 0
      const aLang = a.language === userLang ? -1 : aIdx;
      const bLang = b.language === userLang ? -1 : bIdx;
      if (aLang !== bLang) return aLang - bLang;
    }
    return 0;
  });

  // Group posts by language for "all" view
  const langLabels = { fi: "Suomi", en: "English", fr: "Français", de: "Deutsch", es: "Español" };
  const groupedByLang = blogLang === "all" ? filteredPosts.reduce((acc, post) => {
    const lang = post.language || "fi";
    if (!acc[lang]) acc[lang] = [];
    acc[lang].push(post);
    return acc;
  }, {}) : null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(
      i18n.language === "fi"
        ? "fi-FI"
        : i18n.language === "fr"
        ? "fr-FR"
        : i18n.language === "de"
        ? "de-DE"
        : i18n.language === "es"
        ? "es-ES"
        : "en-US",
      { day: "numeric", month: "long", year: "numeric" }
    );
  };

  const estimateReadTime = (content) => {
    const words = content?.split(/\s+/).length || 0;
    return Math.max(1, Math.round(words / 200));
  };

  const siteUrl = "https://unipäiväkirja.com";

  useSEO({
    title: `${t("blog.heroTitle")} | Uneksia`,
    description: t("blog.heroSubtitle"),
    ogTitle: `${t("blog.heroTitle")} | Uneksia`,
    ogDescription: t("blog.heroSubtitle"),
    ogType: "website",
    ogUrl: `${siteUrl}/blog`,
    canonical: `${siteUrl}/blog`,
    schemaData: posts.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": t("blog.heroTitle"),
      "description": t("blog.heroSubtitle"),
      "url": `${siteUrl}/blog`,
      "publisher": {
        "@type": "Organization",
        "name": "Uneksia",
        "logo": { "@type": "ImageObject", "url": `${siteUrl}/logo-new.png` }
      },
      "blogPost": posts.slice(0, 5).map(p => ({
        "@type": "BlogPosting",
        "headline": p.title,
        "description": p.excerpt,
        "url": `${siteUrl}/blog/${p.slug}`,
        "image": p.cover_image,
        "datePublished": p.created_at,
        "author": { "@type": "Person", "name": p.author_name }
      }))
    } : null
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-white to-green-100">
      {/* Navigation */}
      <nav className="glass-effect border-b border-white/60 sticky top-0 z-50 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="blog-home-link">
              <img src="/logo-new.png" alt="Uneksia" className="w-10 h-10 rounded-full" />
              <span className="font-heading text-xl text-void">Uneksia</span>
            </Link>
            <div className="flex items-center gap-3 sm:gap-5">
              <Link
                to="/#free-trial"
                className="font-body text-sm text-void/70 hover:text-void transition-colors hidden sm:inline"
                data-testid="blog-how-it-works"
              >
                {t("trial.tryAiAnalysis")}
              </Link>
              <LanguageSelector variant="minimal" />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/40 backdrop-blur-md rounded-full border border-white/60">
            <BookOpen className="w-4 h-4 text-lucid" />
            <span className="text-sm font-body text-void">{t("blog.tagline")}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-light text-void tracking-tight">
            {t("blog.heroTitle")}
          </h1>
          <p className="text-lg text-void/70 font-body tracking-wide max-w-2xl mx-auto">
            {t("blog.heroSubtitle")}
          </p>
        </div>
      </section>

      {/* Search and Tags */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <div className="relative w-full sm:w-auto sm:flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-void/40" />
            <Input
              type="text"
              placeholder={t("blog.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/50 border-transparent focus:border-ethereal rounded-xl"
              data-testid="blog-search-input"
            />
          </div>
        </div>
        
        {/* Language filter */}
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="blog-language-filter">
          {[
            { code: "all", label: t("blog.allLanguages") || "All" },
            { code: "fi", label: "Suomi" },
            { code: "en", label: "English" },
            { code: "fr", label: "Fran\u00e7ais" },
            { code: "de", label: "Deutsch" },
            { code: "es", label: "Espa\u00f1ol" },
          ].map(({ code, label }) => (
            <button
              key={code}
              onClick={() => setBlogLang(code)}
              className={`px-3 py-1 rounded-full text-xs font-body transition-all ${
                blogLang === code
                  ? "bg-lucid text-white"
                  : "bg-white/50 text-void/60 hover:bg-white/80 border border-white/60"
              }`}
              data-testid={`blog-lang-${code}`}
            >
              {label}
            </button>
          ))}
        </div>

        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              onClick={() => setSelectedTag(null)}
              className={`px-3 py-1 rounded-full text-sm font-body transition-all ${
                !selectedTag
                  ? "bg-void text-white"
                  : "bg-white/50 text-void/70 hover:bg-white/80"
              }`}
              data-testid="blog-tag-all"
            >
              {t("blog.allPosts")}
            </button>
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag === selectedTag ? null : tag)}
                className={`px-3 py-1 rounded-full text-sm font-body transition-all ${
                  selectedTag === tag
                    ? "bg-void text-white"
                    : "bg-white/50 text-void/70 hover:bg-white/80"
                }`}
                data-testid={`blog-tag-${tag}`}
              >
                <Tag className="w-3 h-3 inline mr-1" />
                {tag}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Posts Grid */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-void/20 border-t-void rounded-full mx-auto" />
            <p className="text-void/60 font-body mt-4">{t("blog.loading")}</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-20 glass-effect rounded-3xl p-12">
            <BookOpen className="w-12 h-12 text-void/30 mx-auto mb-4" />
            <p className="text-void/60 font-body text-lg">{t("blog.noPosts")}</p>
          </div>
        ) : blogLang === "all" && groupedByLang ? (
          /* Grouped by language view */
          <div className="space-y-10">
            {Object.entries(groupedByLang)
              .sort(([a], [b]) => {
                if (a === userLang) return -1;
                if (b === userLang) return 1;
                return 0;
              })
              .map(([lang, langPosts]) => (
              <div key={lang}>
                <h3 className="font-heading text-xl text-void mb-4 flex items-center gap-2">
                  <span className="px-3 py-1 bg-lucid/10 text-lucid rounded-full text-sm font-body">
                    {langLabels[lang] || lang.toUpperCase()}
                  </span>
                  <span className="text-void/30 text-sm font-body">{langPosts.length} {langPosts.length === 1 ? 'artikkeli' : 'artikkelia'}</span>
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {langPosts.map((post) => (
                    <Link
                      to={`/blog/${post.slug}`}
                      key={post.id}
                      className="block group"
                      data-testid={`blog-post-${post.slug}`}
                    >
                      <article className="glass-effect rounded-3xl overflow-hidden hover:shadow-lg transition-all duration-500 hover:-translate-y-1 h-full">
                        {post.cover_image && (
                          <div className="h-48 overflow-hidden">
                            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                          </div>
                        )}
                        <div className="p-6">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            {post.tags?.slice(0, 2).map((tag) => (
                              <span key={tag} className="px-2 py-0.5 bg-ethereal/20 text-void/70 text-xs rounded-full font-body">{tag}</span>
                            ))}
                          </div>
                          <h3 className="text-xl font-heading font-light text-void mb-2 group-hover:text-ethereal transition-colors">{post.title}</h3>
                          <p className="text-void/60 font-body text-sm leading-relaxed mb-3 line-clamp-3">{post.excerpt}</p>
                          <div className="flex items-center gap-3 text-xs text-void/50 font-body">
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{estimateReadTime(post.content)} min</span>
                            <span>{formatDate(post.created_at)}</span>
                          </div>
                        </div>
                      </article>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-8">
            {/* Featured Post (First) */}
            {filteredPosts.length > 0 && (
              <Link
                to={`/blog/${filteredPosts[0].slug}`}
                className="block group"
                data-testid="blog-featured-post"
              >
                <article className="glass-effect rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                  {filteredPosts[0].cover_image && (
                    <div className="h-64 sm:h-80 overflow-hidden">
                      <img
                        src={filteredPosts[0].cover_image}
                        alt={filteredPosts[0].title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  )}
                  <div className="p-8">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      {filteredPosts[0].tags?.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-ethereal/20 text-void/70 text-xs rounded-full font-body"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-heading font-light text-void mb-3 group-hover:text-ethereal transition-colors">
                      {filteredPosts[0].title}
                    </h2>
                    <p className="text-void/70 font-body leading-relaxed mb-4">
                      {filteredPosts[0].excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-void/50 font-body">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {estimateReadTime(filteredPosts[0].content)} min
                      </span>
                      <span>{formatDate(filteredPosts[0].created_at)}</span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {filteredPosts[0].views || 0}
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            )}

            {/* Rest of Posts */}
            <div className="grid md:grid-cols-2 gap-6">
              {filteredPosts.slice(1).map((post) => (
                <Link
                  to={`/blog/${post.slug}`}
                  key={post.id}
                  className="block group"
                  data-testid={`blog-post-${post.slug}`}
                >
                  <article className="glass-effect rounded-3xl overflow-hidden hover:shadow-lg transition-all duration-500 hover:-translate-y-1 h-full">
                    {post.cover_image && (
                      <div className="h-48 overflow-hidden">
                        <img
                          src={post.cover_image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {post.tags?.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-ethereal/20 text-void/70 text-xs rounded-full font-body"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-xl font-heading font-light text-void mb-2 group-hover:text-ethereal transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-void/60 font-body text-sm leading-relaxed mb-3 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-void/50 font-body">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {estimateReadTime(post.content)} min
                        </span>
                        <span>{formatDate(post.created_at)}</span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="glass-effect rounded-3xl p-10 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-light text-void tracking-tight mb-3">
            {t("blog.ctaTitle")}
          </h2>
          <p className="text-void/70 font-body mb-6">{t("blog.ctaSubtitle")}</p>
          <Button
            onClick={() => navigate(token ? "/journal" : "/auth")}
            className="bg-void text-white hover:bg-void/90 rounded-full px-8 py-6 font-body font-medium transition-all duration-300 hover:scale-105 active:scale-95"
            data-testid="blog-cta-start-button"
          >
            {t("blog.ctaButton")}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-void/10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-new.png" alt="Uneksia" className="w-6 h-6" />
              <span className="font-heading text-void">{t("appName")}</span>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/blog" className="text-sm text-void/60 hover:text-void font-body transition-colors">
                {t("blog.title")}
              </Link>
              <Link to="/privacy" className="text-sm text-void/60 hover:text-void font-body transition-colors">
                {t("footer.privacy")}
              </Link>
            </div>
            <p className="text-sm text-void/60 font-body">{t("footer.copyright")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
